// Ids that tie Denver's failure reports to Tokyo's and Kyoto's log lines: a fresh X-Request-ID
// per HTTP attempt (both servers adopt and echo it) and an operation id the caller passes as
// X-Operation-ID for every request of one user operation.
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const newId = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
};

// The request id the server echoed, from a response or an axios error.
export const requestIdOf = responseOrError => {
  const headers = responseOrError && ((responseOrError.response && responseOrError.response.headers) || responseOrError.headers);
  const id = headers && headers['x-request-id'];
  return typeof id === 'string' && UUID.test(id) ? id : undefined;
};

// Whether a request goes to this page's own origin (the desktop app's local proxy), resolving a
// relative URL against axios's base URL like axios does.
export const isSameOrigin = (url, origin, baseURL) => {
  try {
    return new URL(url || '', new URL(baseURL || '/', origin)).origin === origin;
  } catch (error) {
    return false;
  }
};

// One policy for every request (axios and fetch): set at startup by src/index.js.
let policy = async () => false;

export const configureCorrelation = enabled => {
  policy = enabled;
};

const decide = async request => {
  try {
    return await policy(request);
  } catch (error) {
    return false;
  }
};

// Headers for a fetch request: a fresh X-Request-ID and the operation id, or none at all.
export const correlationHeaders = async (url, operationId) => {
  if (!(await decide({ url }))) return {};
  return { 'X-Request-ID': newId(), ...(operationId ? { 'X-Operation-ID': operationId } : {}) };
};

// The policy decides per request; any doubt (an error deciding) means no header, never a failed
// request.
export const installCorrelationHeaders = axiosInstance => {
  axiosInstance.interceptors.request.use(async config => {
    if (await decide(config)) {
      config.headers = { ...config.headers, 'X-Request-ID': newId() };
    } else if (config.headers && 'X-Operation-ID' in config.headers) {
      // Callers always set it; without correlation it must not trigger a CORS preflight.
      const { 'X-Operation-ID': omitted, ...headers } = config.headers;
      config.headers = headers;
    }
    return config;
  });
};
